import type { SportTableRow } from "@/components/SportsTable";
import { countFixtures, getDefinition, listDefinitions } from "@/lib/api/client";

/** Formatted server-side and shipped as text: `toLocaleDateString` in a client
 * component uses the browser's locale, not the server's, and breaks hydration. */
const DATE = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/**
 * One row per sport for the sports table.
 *
 * Definitions arrive one row per *version*, so they are folded down to the live
 * version plus whether an unpublished draft exists. Master counts need the full
 * document, fetched in parallel and allowed to fail — a sport whose definition
 * won't load still belongs in the list.
 */
export async function loadSportRows(): Promise<SportTableRow[]> {
  const [defs, counts] = await Promise.all([listDefinitions(), countFixtures()]);

  const byCode = new Map<string, SportTableRow>();
  for (const d of defs) {
    const row = byCode.get(d.sportCode) ?? {
      code: d.sportCode,
      published: null,
      draft: null,
      publishedAt: null,
      fixtures: 0,
      masters: 0,
      icon: null,
      partOf: d.partOf ?? null,
      groups: d.groups ?? false,
    };
    if (d.status === "PUBLISHED" && (row.published ?? 0) < d.version) {
      row.published = d.version;
      row.publishedAt = d.publishedAt ? DATE.format(new Date(d.publishedAt)) : null;
    }
    if (d.status === "DRAFT" && (row.draft ?? 0) < d.version) row.draft = d.version;
    byCode.set(d.sportCode, row);
  }

  for (const count of counts) {
    const row = byCode.get(count.sportCode);
    if (row) row.fixtures = count.total;
  }

  const codes = [...byCode.keys()];
  const documents = await Promise.all(codes.map((code) => getDefinition(code).catch(() => null)));
  codes.forEach((code, i) => {
    const row = byCode.get(code);
    if (row) {
      row.masters = documents[i]?.masters?.length ?? 0;
      row.icon = documents[i]?.icon ?? null;
    }
  });

  return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code));
}
